import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import React, { useContext } from "react";
import { GlobalContext } from "../Utils/Contexts";
import { cloneTicket, newTicket, TicketDatabase } from "../Utils/TicketDatabase";
import { getPilotAssignmentsByDate } from "../Utils/UtilityMethods";
import { DATE_FORMAT, REVISIONS_ALLOWED_DAYS_BACK } from "../sharedCode/metadata";
import { TicketExtended } from "../Utils/TypeExtensions";
import { bookmarkTicket, getTicketNumber, IsDemoMode } from "../sharedCode/HelperMethods";
import SyncIcon from "@mui/icons-material/Sync";
import { useLiveQuery } from "dexie-react-hooks";
import { JobAssociableAssignmentSummary } from "../sharedCode/avroGraphql";

interface props {
    ticketToClone: TicketExtended | undefined;
    showForceReloadDialogue(): void;
}

export default function AddTicketDialog(props: props): JSX.Element {
    const context = useContext(GlobalContext);
    const jobList = useLiveQuery(async () => TicketDatabase.job.toArray());
    const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());

    if (!jobList || !ticketList) return <></>;

    function handleCancel() {
        context.setShowAddTicketDialogue(false);
    }

    async function handleAdd(missingTicket: missingTicketType) {
        let addedTicket;
        const pilotId = (IsDemoMode() ? context.proxyUser?.id ?? context.loggedInUser.id : context.loggedInUser.id) || -1;

        if (props.ticketToClone === undefined) {
            addedTicket = await newTicket(missingTicket.date, missingTicket.job_id, pilotId).catch(() => props.showForceReloadDialogue());
        } else {
            addedTicket = await cloneTicket(props.ticketToClone, missingTicket.date, missingTicket.job_id, pilotId).catch(() => props.showForceReloadDialogue());
        }

        if (addedTicket) {
            bookmarkTicket(addedTicket);
            context.setSelectedTicketId(addedTicket.id);
        }

        addedTicket = null;

        context.setShowAddTicketDialogue(false);
    }

    const pilot = context.proxyUser ? context.proxyUser : context.loggedInUser;

    const allPilotAssignments: JobAssociableAssignmentSummary[] = [];

    jobList.forEach((j) => {
        const assignments = j.JobAssignments?.filter((a) => {
            return a?.assigned_role === "PILOT" && a.assigned_person_id === pilot.id;
        });

        assignments?.forEach((a) => {
            if (a) allPilotAssignments.push(a);
        });
    });

    type missingTicketType = {
        date: string;
        job_id: number;
    };

    const today = dayjs().startOf("day");
    const missingTickets: Array<missingTicketType> = [];

    for (let i = REVISIONS_ALLOWED_DAYS_BACK; i >= 0; i--) {
        const checkDate = today.subtract(i, "day").format(DATE_FORMAT);
        const assignments = getPilotAssignmentsByDate(allPilotAssignments, checkDate);
        for (const assignment of assignments) {
            const ticket = ticketList.find((t) => t.job_id === assignment.job_id && t.date === checkDate);
            if (ticket === undefined) {
                missingTickets.push({ date: checkDate, job_id: assignment.job_id });
            }
        }
    }

    const createableTickets = [];
    for (const assignment of missingTickets) {
        const job = jobList.find((j) => j.id === assignment.job_id);
        createableTickets.push(`${assignment.date} - ${job?.label}`);
    }

    const ticketButtons = [];
    for (const assignment of missingTickets) {
        const job = jobList.find((j) => j.id === assignment.job_id);
        ticketButtons.push(
            <Button key={`${assignment.date}${assignment.job_id}`} fullWidth sx={{ margin: 1 }} variant="contained" color="secondary" onClick={(e) => handleAdd(assignment)}>
                {`${assignment.date} - ${job?.label}`}
            </Button>
        );
    }

    const isCloning = props.ticketToClone !== undefined;
    const isNoAssignment = ticketButtons.length === 0;
    const titleText = isNoAssignment
        ? "NO JOB ASSIGNMENTS FOUND"
        : isCloning
        ? `TO CLONE ${props.ticketToClone ? getTicketNumber(props.ticketToClone) : ""} TO A NEW TICKET, SELECT DATE - JOB`
        : "TO CREATE A NEW TICKET, SELECT DATE - JOB";

    return (
        <Dialog open={true} onClose={handleCancel} fullWidth={true}>
            <DialogTitle align={"center"} color={isNoAssignment ? "error" : "text"}>
                {titleText}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ border: "1px solid lightgrey", borderRadius: 3, p: 3 }}>
                    {ticketButtons}
                    {isNoAssignment && (
                        <Grid container flexDirection="column" justifyContent={"center"} alignItems={"center"} spacing={2}>
                            <Grid item container flexDirection={"row"} justifyContent={"center"} alignItems={"center"}>
                                <Typography>TAP THE SYNC BUTTON (</Typography>
                                <SyncIcon />
                                <Typography>) TO CHECK FOR UPDATES</Typography>
                            </Grid>
                            <Grid>
                                <Typography style={{ textAlign: "center", marginTop: 16 }}>
                                    IF THE SYNC BUTTON IS ORANGE, SWITCH TO CELLULAR IN YOUR IPAD SETTINGS AND TAP THE SYNC BUTTON AGAIN
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography>IF THERE ARE STILL NO ASSIGNMENTS FOUND, PLEASE CONTACT DISPATCH</Typography>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>

            <DialogActions style={{ justifyContent: "center" }}>
                <Button sx={{ margin: 2 }} variant="contained" color="secondary" onClick={handleCancel}>
                    {isNoAssignment ? "OK" : "CANCEL"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
